import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ERROR_CODES } from '../shared/constants/error-codes';
import { LANGUAGE_CONSTANTS as LANG_CONST } from '../shared/constants/language-constants';
import { ISite, Site } from './site-management-interfaces';
import { Utility } from '../shared/utils/utility.service';
import { StateManagementService } from '../shared/services/state-management.service';
import {
  NotFoundError,
  OperationFailedError,
  UpdateFailedError,
} from '../shared/errors/errors';
import { AddressSuggestions } from '../shared/models/data-interfaces';
import { TranslateService } from '@ngx-translate/core';
import { ICardCredentials } from '../user-management/user-management-interfaces';
import { cardStatusResponse } from '../shared/constants/response-mapping';

@Injectable({ providedIn: 'root' })
export class SiteManagementService {
  private url = environment.graphqlUrl;
  // time to live (cache object) is 30 seconds
  private ttl: number = 60000;
  private initialTime: number = Date.now();

  constructor(
    private http: HttpClient,
    private stateManagementService: StateManagementService,
    private translate: TranslateService
  ) {}

  getAllSites(loader?: boolean): Observable<Site> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query:
            'query GetSites {  getSites { siteId siteName address siteTimeZone commonAccessControl }}',
          variables: {},
          operationName: 'GetSites',
        },
        {
          headers: Utility.loaderHeader(loader),
        }
      )
      .pipe(
        map((res: any) => {
          if (res?.data && res?.data?.getSites?.length > 0) {
            this.initialTime = Date.now();
            let sites: Site = res.data?.getSites?.sort((a: any, b: any) =>
              a?.siteName.localeCompare(b?.siteName)
            );
            sites = sites!.map((site: ISite) => ({
              ...site,
              commonAccessControl:
                site.commonAccessControl === null
                  ? true
                  : site.commonAccessControl,
            }));

            this.stateManagementService.updateSites(sites);
            return sites;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.name) {
            case ERROR_CODES.SITE_NOT_FOUND:
              throw new NotFoundError(
                this.translate.instant('NO_SITE_FOUND_AGAINST_THIS_USER')
              );
            case ERROR_CODES.FAILED:
              throw new NotFoundError(
                this.translate.instant('REQUEST_TO_GET_SITES_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_SITES_FAILED')
              );
          }
        })
      );
  }

  getCardsBySite(
    siteId: string,
    isMaster: number = 1
  ): Observable<ICardCredentials[]> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'query GetCardsBySite($siteId: String!, $isMasterCard: Int) { getCardsBySite(siteId: $siteId, isMasterCard: $isMasterCard) { cardId cardName cardStatus credentialUser cardEncoder badgeId creatorName description } }',
        variables: {
          siteId,
          isMasterCard: isMaster,
        },
        operationName: 'GetCardsBySite',
      })
      .pipe(
        map((res: any) => {
          if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }

          const apiCards = res?.data?.getCardsBySite ?? [];

          const cards = apiCards.map((card: any) => {
            const statusKey =
              card.cardStatus !== undefined
                ? cardStatusResponse[card.cardStatus]
                : undefined;
            const cardStatus = statusKey
              ? this.translate.instant(statusKey)
              : '';
            return {
              ...card,
              cardStatus,
            };
          });

          return cards as ICardCredentials[];
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('REQUEST_TO_GET_USER_CARDS_LIST_FAILED')
              );
            default:
              throw new Error(
                this.translate.instant('REQUEST_TO_GET_USER_CARDS_LIST_FAILED')
              );
          }
        })
      );
  }

  updateSiteDetails(
    siteId: any,
    siteName: any,
    address: any
  ): Observable<ISite | undefined> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation UpdateSite($siteId: String!, $address: String!, $siteName: String!) {  updateSite(siteId: $siteId, address: $address, siteName: $siteName) { siteId  }}',
        variables: {
          siteId: siteId,
          siteName: siteName,
          address: address,
        },
        operationName: 'UpdateSite',
      })
      .pipe(
        map((res: any) => {
          if (res?.data && res?.data?.updateSite) {
            const site: ISite = {
              siteId: res.data.updateSite?.siteId,
              siteName: siteName,
              address: address,
            };
            //updating store as per the updated site
            const sites = this.stateManagementService.getSites();
            sites?.forEach((currentSite) => {
              if (site?.siteId === currentSite?.siteId) {
                currentSite.siteName = site?.siteName;
                currentSite.address = site?.address;
              }
            });
            this.stateManagementService.updateSites(sites);
            return site;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.name) {
            case ERROR_CODES.FAILED:
              throw new UpdateFailedError(
                this.translate.instant('SITE_ALREADY_EXISTS')
              );
            default:
              throw new Error(this.translate.instant('SITE_ALREADY_EXISTS'));
          }
        })
      );
  }

  /**
   * @description This method hit babckend API to get presigned URl
   * @returns  return observable with presigned url
   */
  getSiteBucketPresignedURL(fileType: string): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query:
          'mutation PresignedURL($uploadType: String!, $fileType: String!) {  presignedURL(uploadType: $uploadType, fileType: $fileType)  { fields url imageName }}',
        variables: {
          uploadType: 'site',
          fileType: fileType,
        },
        operationName: 'PresignedURL',
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.presignedURL) {
            let result = res?.data?.presignedURL;
            if (typeof result?.fields === 'string')
              result.fields = JSON.parse(result.fields);
            return result;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('FAILED_TO_GET_PRE_SIGNED_URL')
              );
            default:
              throw new Error(
                this.translate.instant('FAILED_TO_GET_PRE_SIGNED_URL')
              );
          }
        })
      );
  }

  getAddressSuggestions(
    text: String,
    hideLoader?: boolean
  ): Observable<AddressSuggestions> {
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query:
            'query ($text: String!) { getAddressSuggestions(text: $text) { suggestions } }',
          variables: {
            text: text,
          },
        },
        {
          headers: Utility.loaderHeader(hideLoader),
        }
      )
      .pipe(
        map((res: any) => {
          if (res?.data?.getAddressSuggestions) {
            return res?.data?.getAddressSuggestions?.suggestions;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new OperationFailedError(
                this.translate.instant('FAILED_TO_GET_ADDRESS_SUGGESTIONS')
              );
            default:
              throw new Error(
                this.translate.instant('FAILED_TO_GET_ADDRESS_SUGGESTIONS')
              );
          }
        })
      );
  }

  /**
   * @description This upload image to presigned URL
   * @input {Any, FormData}
   * @param { any } preSignedUrl presigned URL generated
   * @param { FormData } fd Form data contains all image information
   * @returns observable
   */
  uploadFile(preSignedUrl: any, fd: FormData): Observable<any> {
    return this.http.post<any>(`${preSignedUrl}`, fd);
  }

  /**
   * @description Get event history by RFID card (badge ID) and site ID
   * @param badgeId The badge/card ID
   * @param siteId The site ID
   * @param filterCriteria Filter criteria for the query
   * @returns Observable with event history
   */
  getEventHistoryByRFIDCard(
    badgeId: string,
    siteId: string,
    filterCriteria: any
  ): Observable<any> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `query($badgeId: String!, $siteId: String!, $filterCriteria: FilterCriteria!) {
          getEventHistoryByRFIDCard(badgeId: $badgeId, siteId: $siteId, filterCriteria: $filterCriteria) {
            events {
              id
              eventCode
              eventDescription
              user
              timestamp
              isoTime
              deviceName
              unitId
              unitName
              floorId
              floorName
              buildingId
              buildingName
              accessPointName
              eventIndex
              wingName
              isIssue
              isAbnormal
              isMaster
              source
              sourceName
              extraDescription
              eventTitle
            }
            totalRecords
            cursor
          }
        }`,
        variables: {
          badgeId: badgeId,
          siteId: siteId,
          filterCriteria: filterCriteria,
        },
      })
      .pipe(
        map((res: any) => {
          if (res?.data?.getEventHistoryByRFIDCard) {
            return res.data.getEventHistoryByRFIDCard;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          throw new Error(
            this.translate.instant('FAILED_TO_GET_EVENT_HISTORY')
          );
        })
      );
  }

  /**
   * @description This method updates the master card description
   * @param { string } badgeId The badge ID of the master card
   * @param { string } siteId The site ID
   * @param { string } description The new description for the master card
   * @returns Observable with success status
   */
  updateMasterCard(
    badgeId: string,
    siteId: string,
    description: string
  ): Observable<{ success: boolean } | undefined> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `mutation UpdateMasterCard($badgeId: String!, $siteId: String!, $description: String) {
          updateMasterCard(badgeId: $badgeId, siteId: $siteId, description: $description) {
            success
          }
        }`,
        variables: {
          badgeId: badgeId,
          siteId: siteId,
          description: description,
        },
        operationName: 'UpdateMasterCard',
      })
      .pipe(
        map((res: any) => {
          if (res?.data && res?.data?.updateMasterCard) {
            return res.data.updateMasterCard;
          } else if (res?.errors?.length > 0) {
            throw new Error(res?.errors[0]?.errorType);
          }
        }),
        catchError((err: any) => {
          switch (err.message) {
            case ERROR_CODES.FAILED:
              throw new UpdateFailedError(
                this.translate.instant('FAILED_TO_UPDATE_MASTER_CARD')
              );
            default:
              throw new Error(
                this.translate.instant('FAILED_TO_UPDATE_MASTER_CARD')
              );
          }
        })
      );
  }
}
